import { useEffect, useState } from 'react';
import { useSavedSearchStore } from 'hooks/use-saved-search-store';
import { SAVED_SEARCHES_FETCH_LIMIT } from './collections-saved-searches';

/**
 * The user's saved-search total, fetched eagerly — independent of which
 * Collections tab is visible — so the Saved Searches tab shows its count as soon
 * as the pane opens. It shares the saved-search React Query cache (same `limit`)
 * with the tab's own list, so it adds no extra request once loaded and stays in
 * sync with renames/deletes made there.
 *
 * Returns `undefined` until the first fetch settles, so no premature "0" shows;
 * afterwards it mirrors the cached list length (which React Query keeps across
 * background refetches), so the count never blinks away.
 *
 * That list length, not a server total: the endpoint does return one in its
 * `meta`, but the repository drops it, and since the tab itself only ever
 * fetches `limit` searches, the length is what the tab can actually show.
 */
export function useSavedSearchesCount(limit: number = SAVED_SEARCHES_FETCH_LIMIT): number | undefined {
  const { recents, fetchRecents } = useSavedSearchStore({ limit });
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchRecents().finally(() => {
      if (!cancelled) setHasLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchRecents]);

  return hasLoaded ? recents.length : undefined;
}
